import React from "react";
import {
  Handshake,
  Clapperboard,
  Target,
  Settings2,
  Rocket,
  Code2,
  ArrowUpRight,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * Bento Tetris layout — flagships dominate top row, supporting services optimised below.
 */
const SERVICES = [
  {
    n: "01",
    icon: Handshake,
    tag: "Flagship Service",
    title: "Partnerships & Sponsorships",
    desc: "Strategic collaborations that accelerate brand visibility — from influencer deals to large-scale event sponsorships.",
    points: [
      "Influencer & Brand Collaborations",
      "Event Sponsorship Strategy",
      "Co-Branding Campaigns",
      "End-to-end Partner Management",
    ],
    span: "col-span-12 lg:col-span-8 lg:row-span-2",
    bg: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: true,
    href: "#showcase-partnerships",
    testid: "service-partnerships",
  },
  {
    n: "02",
    icon: Clapperboard,
    tag: "Flagship Service",
    title: "Video Production & Creative",
    desc: "Brand films, event coverage, social reels — visual content that captures attention and drives action.",
    points: [
      "Brand / Corporate Videos",
      "Event Coverage",
      "Social Media Reels",
      "End-to-end Production",
    ],
    span: "col-span-12 lg:col-span-4 lg:row-span-2",
    bg: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accent: true,
    href: "#showcase-video",
    testid: "service-video",
  },
  {
    n: "03",
    icon: Target,
    tag: "Strategy",
    title: "Marketing Strategy & Growth",
    desc: "Brand positioning, go-to-market strategy and full-funnel campaign design built to scale.",
    points: ["Brand Positioning", "Competitor Analysis", "Full-Funnel Campaigns"],
    span: "col-span-12 md:col-span-6 lg:col-span-6",
    bg: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    testid: "service-strategy",
  },
  {
    n: "04",
    icon: Settings2,
    tag: "Operations",
    title: "Account & Operations",
    desc: "Marketplace account management, listing optimisation and performance insights — owned end-to-end.",
    points: ["Marketplace Mgmt", "Listing Optimization", "Performance Insights"],
    span: "col-span-12 md:col-span-6 lg:col-span-6",
    bg: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    testid: "service-operations",
  },
  {
    n: "05",
    icon: Rocket,
    tag: "Performance",
    title: "Digital Marketing & Performance",
    desc: "Data-driven campaigns balancing creativity and ROI — SEO, paid media and D2C growth across every channel.",
    points: ["SEO & Content", "Social Media", "Google & Meta Ads", "D2C Growth"],
    span: "col-span-12 md:col-span-6 lg:col-span-6",
    bg: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    testid: "service-digital",
  },
  {
    n: "06",
    icon: Code2,
    tag: "Technology",
    title: "Technology & Development",
    desc: "High-performance websites, mobile apps and custom web applications that bring strategy to life.",
    points: ["Web Design & Dev", "Mobile Apps", "Custom Web Apps"],
    span: "col-span-12 md:col-span-6 lg:col-span-6",
    bg: "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    testid: "service-tech",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-16 md:py-20 lg:py-24 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        <Reveal className="text-center mb-10 md:mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-fuchsia-500" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
              Our Core Services
            </span>
            <span className="h-px w-10 bg-fuchsia-500" />
          </div>
          <h2
            data-testid="services-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[64px] font-black tracking-[-0.03em] text-white leading-[0.98] max-w-5xl mx-auto"
          >
            Six disciplines.{" "}
            <span className="text-fuchsia-500">Two flagships.</span>{" "}
            <span className="text-white/40 italic font-light">One growth engine.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-white/55 max-w-3xl mx-auto leading-relaxed font-body">
            Every service is structured to deliver outcomes, not just activity.
            Sponsorships and Video Production lead the stack — the rest amplifies them.
          </p>
        </Reveal>

        <div className="grid grid-cols-12 gap-4 sm:gap-5 md:gap-6 auto-rows-[minmax(220px,auto)]">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.n}
              as="div"
              className={s.span}
              variant="scale"
              delay={Math.min(i * 70, 280)}
            >
              <ServiceCard s={s} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ s }) => {
  const Icon = s.icon;
  return (
    <div
      data-testid={s.testid}
      className="group relative h-full overflow-hidden border border-white/10 bg-[#0c0c10] hover:border-fuchsia-500/50 hover:-translate-y-1 transition-all duration-500"
    >
      {/* Background image layer */}
      {s.bg && (
        <div className="absolute inset-0">
          <img
            src={s.bg}
            alt={s.title}
            className={`w-full h-full object-cover transition-all duration-[1500ms] ease-out ${
              s.accent
                ? "opacity-30 group-hover:opacity-50 group-hover:scale-105"
                : "opacity-[0.08] grayscale group-hover:opacity-[0.18] group-hover:grayscale-0 group-hover:scale-105"
            }`}
          />
          <div
            className={`absolute inset-0 ${
              s.accent
                ? "bg-gradient-to-br from-black/85 via-black/60 to-black/90"
                : "bg-gradient-to-br from-[#0c0c10] via-[#0c0c10]/85 to-[#0c0c10]"
            }`}
          />
          {s.accent && (
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(146,24,136,0.18),transparent_55%)]" />
          )}
          {!s.accent && (
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_bottom_left,rgba(146,24,136,0.14),transparent_55%)]" />
          )}
        </div>
      )}

      {/* Decorative corner number for supporting cards */}
      {!s.accent && (
        <div className="absolute top-0 right-0 pointer-events-none">
          <span className="font-display text-[120px] md:text-[160px] leading-none font-black text-white/[0.025] group-hover:text-fuchsia-500/[0.07] transition-colors duration-700 select-none">
            {s.n}
          </span>
        </div>
      )}

      <div className="relative z-10 p-6 md:p-8 lg:p-9 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-7">
            <span
              className={`text-[10px] uppercase tracking-[0.28em] font-bold ${
                s.accent ? "text-fuchsia-400" : "text-fuchsia-400/80"
              }`}
            >
              {s.tag}
            </span>
            <span className="font-display text-xs text-white/30 tracking-widest">
              {s.n}
            </span>
          </div>

          <Icon
            className={`mb-5 transition-all duration-500 ${
              s.accent ? "w-9 h-9 text-fuchsia-400" : "w-7 h-7 text-white/60 group-hover:text-fuchsia-400 group-hover:scale-110"
            }`}
          />

          <h3
            className={`font-display font-bold tracking-tight text-white mb-3 ${
              s.accent
                ? "text-2xl md:text-3xl lg:text-[36px] leading-[1.05]"
                : "text-xl md:text-[22px] leading-tight"
            }`}
          >
            {s.title}
          </h3>
          <p
            className={`leading-relaxed text-white/60 ${
              s.accent ? "text-sm md:text-base max-w-lg" : "text-sm max-w-md"
            }`}
          >
            {s.desc}
          </p>
        </div>

        <div className="mt-7">
          <ul className="flex flex-wrap gap-2 mb-5">
            {s.points.map((pt) => (
              <li
                key={pt}
                className={`text-[10px] md:text-[11px] uppercase tracking-[0.18em] border px-3 py-1.5 font-medium transition-colors ${
                  s.accent
                    ? "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/[0.07]"
                    : "border-white/12 text-white/55 group-hover:border-fuchsia-500/30 group-hover:text-white/80"
                }`}
              >
                {pt}
              </li>
            ))}
          </ul>

          {s.accent && s.href && (
            <a
              href={s.href}
              className="inline-flex items-center gap-2 text-sm font-semibold text-fuchsia-400 group-hover:gap-3 transition-all"
            >
              Explore service <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>

      {/* Hover accent line */}
      <div className="absolute bottom-0 left-0 h-px w-0 bg-fuchsia-500 group-hover:w-full transition-all duration-700" />
    </div>
  );
};
